import { Controller } from "@hotwired/stimulus";

/**
 * Keyboard navigation for the inbox entry list.
 *
 * Arrow keys move a visual focus ring between entries; Enter opens the
 * focused entry by programmatically clicking it (delegating to the
 * existing inbox#visitEntry action).
 */
export default class InboxKeyboardController extends Controller {
  static targets = ["item"];
  static values = {
    focusIndex: { type: Number, default: -1 },
  };

  declare itemTargets: HTMLElement[];
  declare focusIndexValue: number;

  connect(): void {
    if (!this.element.getAttribute("role")) {
      this.element.setAttribute("role", "listbox");
    }
    if (!this.element.getAttribute("tabindex")) {
      this.element.setAttribute("tabindex", "0");
    }

    this.itemTargets.forEach((item) => {
      if (!item.getAttribute("role") || item.getAttribute("role") === "button") {
        item.setAttribute("role", "option");
      }
    });
  }

  /**
   * Main keydown handler bound via data-action="keydown->inbox-keyboard#navigate".
   */
  navigate(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.moveDown();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveUp();
        break;
      case "Enter":
        this.openFocused();
        break;
    }
  }

  /**
   * Called automatically by Stimulus when focusIndexValue changes.
   */
  focusIndexValueChanged(): void {
    this.updateFocus();
  }

  // -- Private helpers -------------------------------------------------------

  private moveDown(): void {
    const maxIndex = this.itemTargets.length - 1;
    if (maxIndex < 0) return;

    const next = this.focusIndexValue + 1;
    this.focusIndexValue = Math.min(next, maxIndex);
    this.updateFocus();
  }

  private moveUp(): void {
    if (this.itemTargets.length === 0) return;

    const prev = this.focusIndexValue - 1;
    this.focusIndexValue = Math.max(prev, 0);
    this.updateFocus();
  }

  private openFocused(): void {
    const index = this.focusIndexValue;
    if (index < 0 || index >= this.itemTargets.length) return;

    this.itemTargets[index].click();
  }

  private updateFocus(): void {
    const ringClasses = ["ring-2", "ring-primary", "ring-offset-2"];

    // Remove focus ring from all items
    this.itemTargets.forEach((item) => {
      item.classList.remove(...ringClasses);
    });

    const index = this.focusIndexValue;
    if (index < 0 || index >= this.itemTargets.length) {
      this.element.removeAttribute("aria-activedescendant");
      return;
    }

    const focused = this.itemTargets[index];
    focused.classList.add(...ringClasses);
    if (typeof focused.scrollIntoView === "function") {
      focused.scrollIntoView({ block: "nearest" });
    }

    if (focused.id) {
      this.element.setAttribute("aria-activedescendant", focused.id);
    }
  }
}
